import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle, Loader, PenLine, Send, Star } from "lucide-react";
import { buildWhatsAppUrl } from "../../utils/whatsapp";
import { firmaRecordada } from "../../data/autorizacion";

/**
 * Formulario de experiencia en una entrega: estrellas + comentario.
 *
 * Envía a /.netlify/functions/send-experiencia, que guarda en Supabase
 * (tabla omega_experiencias). El navegador no tiene clave de Supabase.
 *
 * Solo las estrellas son obligatorias: pedir nombre y comentario a la fuerza
 * baja la tasa de respuesta, y una calificación sin texto sigue sirviendo.
 * Publicar el comentario en la web exige el permiso explícito de la casilla
 * — por defecto desmarcada —, igual que la autorización de uso de imagen.
 *
 * PORTAFOLIO. La pregunta arranca en "No": las fotos de un encargo privado
 * son privadas salvo que el cliente diga lo contrario. El "Sí" no publica
 * nada por sí solo: pide el nombre y, al enviar, ofrece firmar ahí mismo la
 * autorización en /entregas/<evento>/autorizacion (AutorizacionPage), que es
 * lo que exige docs/autorizacion-uso-imagen.md (y la firma de los padres si
 * aparecen menores). Firmar en el momento es lo que funciona: una semana
 * después la mitad ya no contesta.
 */

const ENDPOINT = "/.netlify/functions/send-experiencia";

const ETIQUETAS = ["", "Mala", "Regular", "Buena", "Muy buena", "Excelente"];

const fieldClass =
  "w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-navy-900 outline-none transition focus:border-navy-500";
const labelClass = "mb-2 block text-sm font-semibold text-navy-900";

const MENSAJE_AYUDA = "Hola Omega, quise dejar mi experiencia en la página de entrega y no se envió.";

/*
 * UNA VEZ ENVIADA, EL FORMULARIO NO VUELVE. Se recuerda en este navegador
 * (localStorage, por entrega) que ya la dejó y si dijo que sí al portafolio,
 * para seguir ofreciéndole el botón de firma. En otro teléfono el formulario
 * aparece de nuevo: no hay cuenta con que reconocerlo, y un segundo envío es
 * un mal menor frente a pedir datos para evitarlo. Si el almacenamiento está
 * bloqueado (modo privado), simplemente no se recuerda.
 */
const claveGuardada = (entrega) => `omega:experiencia:${entrega}`;

export const experienciaGuardada = (entrega) => {
  try {
    const valor = window.localStorage.getItem(claveGuardada(entrega));
    return valor ? JSON.parse(valor) : null;
  } catch {
    return null;
  }
};

const recordarEnvio = (entrega, portafolio) => {
  try {
    window.localStorage.setItem(
      claveGuardada(entrega),
      JSON.stringify({ portafolio, enviada: new Date().toISOString() })
    );
  } catch {
    /* Sin almacenamiento el agradecimiento se ve igual; solo no se recuerda. */
  }
};

const ExperienciaForm = ({ entrega, onEnviada }) => {
  const [guardada] = useState(() => experienciaGuardada(entrega));
  const [estrellas, setEstrellas] = useState(0);
  const [hover, setHover] = useState(0);
  const [comentario, setComentario] = useState("");
  const [nombre, setNombre] = useState("");
  const [autoriza, setAutoriza] = useState(false);
  const [portafolio, setPortafolio] = useState(false);
  const [trampa, setTrampa] = useState("");
  const [estado, setEstado] = useState({ tipo: null, mensaje: "" });
  const [enviando, setEnviando] = useState(false);

  const visibles = hover || estrellas;

  const enviar = async (event) => {
    event.preventDefault();
    if (!estrellas) {
      setEstado({ tipo: "error", mensaje: "Elige de 1 a 5 estrellas." });
      return;
    }
    if (portafolio && !nombre.trim()) {
      setEstado({
        tipo: "error",
        mensaje: "Escribe tu nombre para enviarte la autorización de las fotos.",
      });
      return;
    }
    setEnviando(true);
    setEstado({ tipo: null, mensaje: "" });
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entrega,
          estrellas,
          comentario,
          nombre,
          autoriza_publicar: autoriza,
          autoriza_portafolio: portafolio,
          sitio_web: trampa,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo enviar");
      recordarEnvio(entrega, portafolio);
      setEstado({ tipo: "ok", mensaje: "" });
      if (onEnviada) onEnviada();
    } catch (error) {
      /* Solo un fallo del servidor ofrece WhatsApp; un campo que falta se
         corrige en el mismo formulario. */
      setEstado({
        tipo: "error",
        mensaje: "No pudimos guardar tu experiencia. Inténtalo de nuevo en un momento.",
        ayuda: true,
      });
    } finally {
      setEnviando(false);
    }
  };

  if (estado.tipo === "ok" || guardada) {
    const quierePortafolio = estado.tipo === "ok" ? portafolio : Boolean(guardada.portafolio);
    return (
      <div className="flex items-start gap-3" role="status">
        <CheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-sand-500" aria-hidden="true" />
        <div>
          <p className="font-display text-xl font-bold text-navy-900">¡Gracias por contarnos!</p>
          <p className="mt-2 text-navy-600">
            Tu experiencia nos ayuda a seguir mejorando cada entrega.
          </p>
          {quierePortafolio && firmaRecordada(entrega) ? (
            <p className="mt-4 text-navy-600">Tu autorización para el portafolio ya está firmada.</p>
          ) : quierePortafolio ? (
            <>
              <p className="mt-4 text-navy-600">
                Para mostrar tus fotos en el portafolio falta tu firma. Toma un minuto y puedes hacerlo
                desde el celular.
              </p>
              <Link to={`${entrega}/autorizacion`} className="btn-omega mt-5">
                <PenLine className="h-4 w-4" aria-hidden="true" />
                Firmar la autorización
              </Link>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={enviar} noValidate>
      <fieldset>
        <legend className={labelClass}>¿Cómo calificas tu experiencia?</legend>
        <div
          className="flex items-center gap-1"
          role="radiogroup"
          aria-label="Calificación de 1 a 5 estrellas"
          onMouseLeave={() => setHover(0)}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={estrellas === n}
              aria-label={`${n} ${n === 1 ? "estrella" : "estrellas"}: ${ETIQUETAS[n]}`}
              onClick={() => setEstrellas(n)}
              onMouseEnter={() => setHover(n)}
              className="rounded-lg p-1 transition hover:scale-110"
            >
              <Star
                className={`h-8 w-8 transition-colors ${
                  n <= visibles ? "fill-sand-400 text-sand-400" : "fill-transparent text-navy-200"
                }`}
                aria-hidden="true"
              />
            </button>
          ))}
          <span className="ml-3 min-w-[6rem] text-sm font-medium text-navy-500" aria-live="polite">
            {ETIQUETAS[visibles]}
          </span>
        </div>
      </fieldset>

      <div className="mt-6">
        <label htmlFor="experiencia-comentario" className={labelClass}>
          Cuéntanos más <span className="font-normal text-navy-400">(opcional)</span>
        </label>
        <textarea
          id="experiencia-comentario"
          rows={4}
          maxLength={2000}
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder="¿Qué te pareció la sesión y la entrega de las fotos?"
          className={`${fieldClass} resize-y`}
        />
      </div>

      <div className="mt-5">
        <label htmlFor="experiencia-nombre" className={labelClass}>
          Tu nombre{" "}
          <span className="font-normal text-navy-400">
            {portafolio ? "(para enviarte la autorización)" : "(opcional)"}
          </span>
        </label>
        <input
          id="experiencia-nombre"
          type="text"
          maxLength={120}
          autoComplete="name"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className={fieldClass}
        />
      </div>

      {/* Trampa para bots: fuera de pantalla y fuera del orden de tabulación. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor="experiencia-sitio">No llenar este campo</label>
        <input
          id="experiencia-sitio"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={trampa}
          onChange={(e) => setTrampa(e.target.value)}
        />
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-navy-600">
        <input
          type="checkbox"
          checked={autoriza}
          onChange={(e) => setAutoriza(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
        />
        Autorizo a Omega Digital a publicar mi comentario en su sitio web.
      </label>

      <fieldset className="mt-6 border-t border-navy-100 pt-6">
        <legend className={labelClass}>
          ¿Podemos mostrar algunas de tus fotos en el portafolio de Omega Digital?
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { valor: false, titulo: "No, prefiero que sean privadas", detalle: "Solo las ves tú y quien tenga el enlace." },
            { valor: true, titulo: "Sí, me encantaría", detalle: "Firmas una autorización en línea antes de que publiquemos nada." },
          ].map((opcion) => (
            <label
              key={String(opcion.valor)}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm transition ${
                portafolio === opcion.valor
                  ? "border-navy-900 bg-navy-50"
                  : "border-navy-200 bg-white hover:border-navy-400"
              }`}
            >
              <input
                type="radio"
                name="experiencia-portafolio"
                checked={portafolio === opcion.valor}
                onChange={() => setPortafolio(opcion.valor)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
              />
              <span>
                <span className="block font-semibold text-navy-900">{opcion.titulo}</span>
                <span className="mt-1 block text-navy-500">{opcion.detalle}</span>
              </span>
            </label>
          ))}
        </div>
        {portafolio ? (
          <p className="mt-3 text-sm text-navy-500">
            Nada se publica sin tu firma. Si en las fotos aparecen menores de edad, la autorización la
            firman sus padres. Puedes retirarla cuando quieras.
          </p>
        ) : null}
      </fieldset>

      {estado.tipo === "error" ? (
        <div className="mt-5 flex items-start gap-2 text-sm text-red-700" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>
            {estado.mensaje}{" "}
            {estado.ayuda ? (
              <a
                href={buildWhatsAppUrl(MENSAJE_AYUDA)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-4"
              >
                O cuéntanoslo por WhatsApp.
              </a>
            ) : null}
          </p>
        </div>
      ) : null}

      <button type="submit" disabled={enviando} className="btn-omega mt-6">
        {enviando ? (
          <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Send className="h-4 w-4" aria-hidden="true" />
        )}
        {enviando ? "Enviando…" : "Enviar mi experiencia"}
      </button>
    </form>
  );
};

export default ExperienciaForm;
